import { Auth0AccountRole, FormSummary, FormsService } from "client/openapi";
import { Select } from "components/Select";
import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";

export type FormChoiceState = {
  formId: number | null;
  sms: boolean;
  email: boolean;
};

export const NO_FORM: FormChoiceState = {
  formId: null,
  sms: true,
  email: false,
};

/** A tick drawn like the ones in the message dialog, with its label. */
export function Tick({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="flex items-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          appearance: "none",
          width: "18px",
          height: "18px",
          border: "2px solid #4ade80",
          borderRadius: "4px",
          backgroundColor: checked ? "#4ade80" : "#fff",
          cursor: "pointer",
        }}
        disabled={disabled}
      />
      <span className="ml-2">{children}</span>
    </label>
  );
}

/**
 * Text and Email ticks. With `show`, only the channels it allows (say, the
 * ones the person has on file).
 */
export function ChannelChecks({
  sms,
  email,
  onChange,
  disabled,
  show = { sms: true, email: true },
}: {
  sms: boolean;
  email: boolean;
  onChange: (next: { sms: boolean; email: boolean }) => void;
  disabled?: boolean;
  show?: { sms: boolean; email: boolean };
}) {
  return (
    <div className="flex gap-4">
      {show.sms && (
        <Tick
          checked={sms}
          onChange={(checked) => onChange({ sms: checked, email })}
          disabled={disabled}
        >
          SMS
        </Tick>
      )}
      {show.email && (
        <Tick
          checked={email}
          onChange={(checked) => onChange({ sms, email: checked })}
          disabled={disabled}
        >
          Email
        </Tick>
      )}
    </div>
  );
}

/** Tutors get tutors forms; parents and students get families forms. */
export function audienceFor(
  personType: Auth0AccountRole
): FormSummary.audience {
  return personType === Auth0AccountRole.ORG_TUTOR
    ? FormSummary.audience.TUTORS
    : FormSummary.audience.FAMILIES;
}

/** What an empty picker says. */
export function noFormsText(audience: FormSummary.audience): string {
  return audience === FormSummary.audience.TUTORS
    ? "No forms for tutors yet"
    : "No forms for families yet";
}

/**
 * The organization's forms (undefined while loading), loaded once per
 * place; with an audience, only that audience's forms.
 */
export function useForms(
  orgId?: number | null,
  audience?: FormSummary.audience
) {
  const [forms, setForms] = useState<FormSummary[]>();
  useEffect(() => {
    if (!orgId) return;
    FormsService.getForms({ orgId })
      .then((list) =>
        setForms(audience ? list.filter((f) => f.audience === audience) : list)
      )
      .catch(() => setForms([]));
  }, [orgId, audience]);
  return forms;
}

/**
 * A picker of the organization's forms. With none, it is shown disabled as
 * "No forms yet" with a link to make one.
 */
export function FormPicker({
  forms,
  formId,
  onChange,
  placeholder,
  disabled,
  emptyText = "No forms yet",
}: {
  forms?: FormSummary[];
  formId: number | null;
  onChange: (formId: number | null) => void;
  placeholder: string;
  disabled?: boolean;
  emptyText?: string;
}) {
  const none = forms !== undefined && forms.length === 0;
  const options = (forms ?? []).map((form) => ({
    value: String(form.id),
    label: form.name,
  }));
  return (
    <>
      <Select
        options={options}
        value={options.find((o) => Number(o.value) === formId) || null}
        onChange={(option) => onChange(option ? Number(option.value) : null)}
        isClearable
        placeholder={none ? emptyText : placeholder}
        isDisabled={disabled || forms === undefined || none}
      />
      {none && (
        <Link
          to="/forms"
          className="mt-1 inline-block text-sm font-medium text-gray-700 underline"
        >
          Create a form
        </Link>
      )}
    </>
  );
}

/**
 * Pick a form to send and how (SMS and/or Email). Used in the invite
 * dialogs, with the forms for the person being invited.
 */
export default function SendFormControls({
  orgId,
  audience,
  value,
  onChange,
  disabled,
}: {
  orgId?: number | null;
  audience: FormSummary.audience;
  value: FormChoiceState;
  onChange: (value: FormChoiceState) => void;
  disabled?: boolean;
}) {
  const forms = useForms(orgId, audience);

  return (
    <div className="mt-4">
      <span className="input-label">Send a form (optional)</span>
      <FormPicker
        forms={forms}
        formId={value.formId}
        onChange={(formId) => onChange({ ...value, formId })}
        placeholder="No form selected"
        disabled={disabled}
        emptyText={noFormsText(audience)}
      />
      {value.formId !== null && (
        <div className="mt-2">
          <ChannelChecks
            sms={value.sms}
            email={value.email}
            onChange={(channels) => onChange({ ...value, ...channels })}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
